import * as React from 'react';

import { MailIcon, RefreshCcw, Stethoscope } from 'lucide-react';

import { Button } from '@indi/ui';
import { logger } from '@indi/utils';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false,
    error: undefined,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    logger.error({
      tag: 'Error Boundary',
      message: 'Error caught by ErrorBoundary',
      error,
      metadata: { errorInfo },
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  private handleContactSupport = () => {
    const subject = encodeURIComponent('Error Report: Application Error');
    const body = encodeURIComponent(`Error Details:\n${this.state.error?.message || 'No error message available'}`);
    window.location.href = `mailto:indiclinic@indiclinic.com?subject=${subject}&body=${body}`;
  };

  public render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-blue-50 to-white">
            <div className="w-full max-w-xl rounded-2xl border border-blue-100 bg-white p-8 shadow-xl">
              <div className="flex flex-col items-center text-center">
                <div className="mb-6 rounded-full bg-blue-50 p-4">
                  <Stethoscope className="h-12 w-12 text-blue-600" />
                </div>

                <h2 className="mb-3 text-3xl font-bold text-gray-800">Oops! Something went wrong</h2>
                <p className="mb-6 max-w-md text-gray-600">
                  We apologize for the inconvenience. Please try again or contact support if the problem persists.
                </p>

                {this.state.error && (
                  <div className="mb-6 w-full">
                    <details className="rounded-xl bg-blue-50 p-4">
                      <summary className="flex cursor-pointer items-center justify-center gap-2 font-medium text-blue-700">
                        <span className="rounded-full bg-blue-100 px-3 py-1 text-sm">Technical Details</span>
                      </summary>
                      <pre className="mt-3 rounded-lg bg-white p-4 text-sm text-gray-600">
                        {this.state.error.message}
                      </pre>
                    </details>
                  </div>
                )}

                <div className="flex w-full max-w-md flex-col gap-4 sm:flex-row">
                  <Button variant="primary" onClick={this.handleReset} size="lg">
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    Quick Recovery
                  </Button>
                  <Button onClick={this.handleContactSupport} variant="outline" size="lg">
                    <MailIcon className="mr-2 h-4 w-4" />
                    Contact Help Desk
                  </Button>
                </div>

                <p className="mt-6 text-sm text-gray-500">
                  Need immediate assistance? Our support team is available 24/7
                </p>
              </div>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
